import React, { FC, ReactNode, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useLayout } from "../../hooks/use-layout";
import { useLocale } from "../../hooks/use-locale";

import { Body, Footer, Header, Toggle } from "@in/component-library";
import { Icon } from "@innovationnorway/lighthouse-components";
import { AppContext } from "../../../app-context";
import { setLocalStorage } from "../../../utils/localstorage";
import { sendSiteimproveEvent } from "../../../utils/sendSiteimproveEvent";
import CookieSettings from "../../components/cookie-settings/cookie-settings";
import Loader from "../../components/loader/loader";
import { DEFAULT_BETAPATH, DEFAULT_PATH, useReturnUrl } from "../../hooks/use-return-url";
import SkipToContent from "../skip-to-content/skip-to-content";

interface LayoutProps {
  showMenu?: boolean;
  children?: ReactNode;
}

const Layout: FC<LayoutProps> = ({ showMenu = true, children }) => {
  const navigate = useNavigate();
  const { account } = useContext(AppContext);
  const { returnUrl } = useReturnUrl();
  const { locale, setLocale } = useLocale();
  const { layout, loading, translations, mutate } = useLayout();
  const { featureFlags, betaWhitelist } = useContext(AppContext);
  const data = false;

  useEffect(() => {
    mutate();
  }, [locale]);

  return loading || !layout ? (
    <Loader />
  ) : (
    <div className="page">
      <span className="top-element" tabIndex={-1} />
      <SkipToContent />
      <Header
        name={account?.name}
        companyName={showMenu ? account?.companyName : undefined}
        loggedInAsLabel={layout.loggedInHeading}
        logoAriaLabel={layout.logoAriaLabel}
        logoLink={layout.logoUrl}
        closeDropdownOnClick={true}
        hotJarClass="data-hj-suppress"
        languages={[
          {
            code: "nb",
            isSelected: locale === "nb",
            name: "Bokmål",
            onClick: () => setLocale("nb")
          },
          {
            code: "nn",
            isSelected: locale === "nn",
            name: "Nynorsk",
            onClick: () => setLocale("nn")
          },
          {
            code: "en",
            isSelected: locale === "en",
            name: "English",
            onClick: () => setLocale("en")
          }
        ]}
        menuItems={[
          {
            text: layout.chooseAccountLabel,
            onClick: () => navigate("/choose-account")
          },
          ...layout.menuItems
            .filter((menuItem) => showMenu || menuItem.requiresAccount !== true)
            .map((menuItem) => ({
              text: menuItem.text,
              onClick: () => (menuItem.isExternal ? window.location.assign(menuItem.href) : navigate(menuItem.href))
            })),
          {
            text: layout.logOutLabel,
            onClick: () => navigate("/single-sign-out")
          }
        ]}
        navItems={
          showMenu
            ? layout.navigationItems?.map((navigationItem) => ({
                text: navigationItem.text,
                href: navigationItem.href,
                active: window.location.pathname === navigationItem.href,
                onClick: () => sendSiteimproveEvent("MyPage-HeaderNavButtons", "GoToPage", navigationItem.text)
              }))
            : []
        }
      />
      {featureFlags?.featureBetaModeEnabled &&
        betaWhitelist &&
        (returnUrl === DEFAULT_PATH || returnUrl === DEFAULT_BETAPATH) && (
          <div className="beta">
            <div className="beta__header">
              <Icon className="beta__header__icon" prefix="fasr" iconName="party-horn" />
              {translations.betaReadyLabel}
            </div>
            <Toggle
              className="beta__toggle"
              value={data}
              onClick={() => {
                sendSiteimproveEvent(`MyPage-betaActivation`, "BetaClicked", "Beta aktivert");
                setLocalStorage("beta", "true");
                navigate("/beta");
              }}
              label="Betaversjon"
            />
          </div>
        )}

      <Body id="maincontent">{children}</Body>
      <Footer className="layout__footer">
        <div dangerouslySetInnerHTML={{ __html: layout.footerText1 }} />
        <div>
          <div dangerouslySetInnerHTML={{ __html: layout.footerText2 }} />
          <CookieSettings />
        </div>
      </Footer>
    </div>
  );
};

export default Layout;
